import React from "react";
import PropTypes from "prop-types";
import { Box, Stack, Typography, Chip } from "@mui/material";

import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import colors from "../../../../../utils/colors";
import { actionLabel } from "../../../../../utils/wafEventV3";
import {
  drawerSeverityPalette,
  TEXT_PRIMARY,
  TEXT_SECONDARY,
  BLOCKED_STATUS_FG,
  isBlockedAction,
} from "../wafEventDrawerShared";
import { EventStatusTagRow } from "./DetectionSummaryPanel";
import {
  spBlockedBannerSx,
  spConfidenceCardSx,
  spBlockIconBoxSx,
  spCriticalBadgeSx,
  spLh,
} from "./smartParseDrawerUi";

function ConfidenceRing({ value }) {
  const pct = Math.min(100, Math.max(0, Number(value) || 0));
  const size = 74;
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (pct / 100) * circumference;

  return (
    <Stack alignItems="center" spacing={0.25}>
      <Box
        sx={{
          width: size,
          height: size,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)", overflow: "visible" }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="rgba(20, 22, 60, 0.10)"
            strokeWidth={stroke}
          />
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={colors.green[200]}
            strokeWidth={stroke}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            style={{ filter: "drop-shadow(0 0 4px rgba(31,193,107,0.5))" }}
          />
        </svg>
        <Typography
          sx={{
            position: "absolute",
            fontSize: 19,
            fontWeight: 700,
            lineHeight: spLh.tight,
            color: TEXT_PRIMARY,
            letterSpacing: "-0.02em",
            display: "flex",
            alignItems: "baseline",
          }}
        >
          {pct}
          <Typography component="span" sx={{ fontSize: 11, fontWeight: 600, ml: "1px", mt: "5px" }}>
            %
          </Typography>
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: 9,
          fontWeight: 700,
          lineHeight: spLh.tight,
          color: colors.green[300],
          textTransform: "uppercase",
          letterSpacing: "0.1em",
        }}
      >
        Confidence
      </Typography>
    </Stack>
  );
}

function BlockSubtitle({ event }) {
  const status = event?.target?.response_status;
  const stage = event?.assessment?.attack_stage;
  const intentName = event?.primary_intent?.intent_name;

  const parts = [];
  if (status != null && status !== "") {
    parts.push({ type: "status", value: status });
  }
  if (stage) {
    parts.push({ type: "text", value: `${stage} stage` });
  }
  if (intentName) {
    parts.push({ type: "text", value: intentName });
  }

  if (parts.length === 0) return null;

  const metaSx = { fontSize: 12.5, lineHeight: spLh.relaxed, color: TEXT_SECONDARY };

  return (
    <Typography sx={metaSx}>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <Typography component="span" sx={metaSx}>
              {" • "}
            </Typography>
          )}
          {part.type === "status" ? (
            <>
              <Typography component="span" sx={metaSx}>HTTP </Typography>
              <Typography component="span" sx={{ color: colors.red[100], fontWeight: 600 }}>
                {part.value}
              </Typography>
            </>
          ) : (
            <Typography component="span" sx={metaSx}>{part.value}</Typography>
          )}
        </React.Fragment>
      ))}
    </Typography>
  );
}

export default function EventStatusBanner({ event }) {
  if (!event) return null;

  const { assessment } = event;
  const severityPalette = drawerSeverityPalette(assessment?.severity);
  const showGauge = assessment?.confidence != null && assessment?.confidence !== "";
  const actionText = actionLabel(assessment?.action);
  const isBlocked = isBlockedAction(assessment?.action);

  return (
    <Stack sx={{ gap: "14px" }}>
      <Stack direction="row" alignItems="stretch" sx={{ gap: "14px" }}>
        <Box sx={spBlockedBannerSx}>
          <Stack direction="row" sx={{ gap: "15px", alignItems: "center", minWidth: 0, position: "relative", zIndex: 1 }}>
            <Box sx={spBlockIconBoxSx}>
              <LayerdIcon icon={DETECTION_ICONS.requestBlocked} size={24} color={colors.red[100]} />
            </Box>
            <Stack sx={{ minWidth: 0, flex: 1, gap: "8px" }}>
              <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap sx={{ gap: "9px" }}>
                <Typography
                  sx={{
                    fontSize: 21,
                    fontWeight: 700,
                    lineHeight: spLh.tight,
                    letterSpacing: "-0.01em",
                    color: isBlocked ? BLOCKED_STATUS_FG : TEXT_PRIMARY,
                  }}
                >
                  {actionText}
                </Typography>
                {assessment?.severity && (
                  <Chip
                    size="small"
                    label={severityPalette.label.toUpperCase()}
                    sx={spCriticalBadgeSx}
                  />
                )}
              </Stack>
              <BlockSubtitle event={event} />
            </Stack>
          </Stack>
        </Box>

        {showGauge && (
          <Box sx={spConfidenceCardSx}>
            <ConfidenceRing value={assessment.confidence} />
          </Box>
        )}
      </Stack>

      <EventStatusTagRow event={event} />
    </Stack>
  );
}

EventStatusBanner.propTypes = {
  event: PropTypes.object,
};
